import React, { useEffect, useRef } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { ResponsiveValues } from '@sketch/global-styles'

import {
  useFlag,
  routes,
  useUserProfile,
  useFolderTerm,
  isProjectRoute,
  isDraftsRoute,
} from '@sketch/modules-common'

import { Breakpoint, pluralize } from '@sketch/components'
import EmptyState from '../EmptyStateOld'

import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
  RenderSharesDropdown,
} from '../DocumentItem'

import {
  GetStorageItemsQueryVariables,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'

import {
  ActionTableCell,
  Footer,
  HeaderTableCell,
  FocusLink,
} from './DocumentItemsLayout.styles'
import {
  DraggableShareItem,
  DraggableShareItemProps,
} from '../DraggableShareItem'
import { useDropShareToNestedProject } from '../../hooks/useDropdShareToNestedProject'
import { useDropShareToCollection } from '../../hooks/useDropShareToCollection'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { useSortingSettings } from '../../hooks/useSortSettings'
import { parseFilters } from '../../../projects/hooks/useProject'
import { StorageItemsLayout } from '../../../storageItems/components'

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

interface DocumentItemsLayoutProps<S> extends CustomColumns {
  workspaceId: string
  shares: S[]
  showProjectName: boolean
  showCollectionName?: boolean
  onLoadMore: () => Promise<any>
  totalCount: number
  renderDropdown: RenderSharesDropdown<S>
  action?: (share: S) => (() => void) | 'link'
  search?: string
  canUseDesignSystemManager?: boolean
  projectId?: string
  disableDrag?: boolean
}

export const DocumentItemsLayout = <S extends StorageItemFragment>(
  props: DocumentItemsLayoutProps<S>
) => {
  const {
    workspaceId,
    shares,
    showProjectName,
    onLoadMore,
    action: actionCreator = () => 'link',
    totalCount,
    renderDropdown,
    search,
    customColumns,
    projectId,
    showCollectionName,
    disableDrag = false,
  }: typeof props = props

  const isDesignSystemsOn = useFlag('design-systems')
  const isNestedProjectsOn = useFlag('nested-projects')

  // Route match
  const { path } = useRouteMatch()
  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)
  const isValidViewToCreateProject = isProjectView || isDraftView

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<any>>(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  const folderTerm = useFolderTerm()

  const { sort } = useSortingSettings()
  const { filters } = useStorageItemsFilters()

  // Drag and Drop
  const storageItemsQueryVariables = {
    projectIdentifier: projectId || '',
    search,
    ...parseFilters(filters, search),
    after: null,
    sharesSortOrder: sort,
  } satisfies GetStorageItemsQueryVariables

  const dropdShareToNestedProject = useDropShareToNestedProject({
    projectIdentifier: projectId,
    storageItemsQueryVariables,
  })
  const dropdShareToCollection = useDropShareToCollection({
    projectIdentifier: projectId,
  })

  const renderGridItem = (item: S, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    const commonDraggableShareItemProps: DraggableShareItemProps<StorageItemFragment> =
      {
        presentation: 'grid',
        workspaceIdentifier: workspaceId,
        item,
        // TODO: Fix this type
        // https://linear.app/sketch/issue/SWEB-1363
        renderDropdown: renderDropdown as any,
        showProjectName,
        showCollectionName: Boolean(showCollectionName),
        draggable: isMobile || !userCanUpdateSettings || disableDrag,
        onShareDropped: isNestedProjectsOn
          ? isValidViewToCreateProject
            ? dropdShareToNestedProject
            : undefined
          : dropdShareToCollection,
      }

    if (action !== 'link') {
      return (
        <DraggableShareItem
          {...commonDraggableShareItemProps}
          onClick={() => action()}
        />
      )
    }

    return (
      <FocusLink
        draggable={false}
        to={{
          pathname: routes.SHARE_VIEW.create({ shareID: item.identifier }),
          state: { componentsView: true },
        }}
      >
        <DraggableShareItem
          {...commonDraggableShareItemProps}
          userIdentifier={userIdentifier}
        />
      </FocusLink>
    )
  }

  const renderListItem = (item: S) => {
    const action = actionCreator(item)

    return (
      <DraggableShareItem
        presentation="list"
        workspaceIdentifier={workspaceId}
        userIdentifier={userIdentifier}
        item={item}
        renderDropdown={renderDropdown}
        draggable={!disableDrag}
        showProjectName={showProjectName}
        showCollectionName={showCollectionName}
        onClick={() => action !== 'link' && action()}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    // for design systems search: check if we're on the library route and if empty count for a special message
    if (isWorkspaceLibrariesRoute && totalCount === 0 && isDesignSystemsOn) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    const commontStorageItemsLayoutProps = {
      items: shares,
      onLoadMore: onLoadMore,
      customColumns: customColumns,
      totalCount: totalCount,
    }

    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Document', 'Documents', totalCount)

      return (
        <StorageItemsLayout
          tableColumns={[
            {
              label: `${totalCount} ${headerLabel}`,
              headerCell: HeaderTableCell,
            },
            { label: folderTerm.upper, headerCell: HeaderTableCell },
            ...(showCollectionName
              ? [{ label: 'Collection', headerCell: HeaderTableCell }]
              : []),
            { label: 'Last Updated', headerCell: HeaderTableCell },
            { label: 'Updated By', headerCell: HeaderTableCell },
            { label: '', headerCell: ActionTableCell },
          ]}
          Skeleton={DocumentTableItemPlaceholder}
          renderItem={item => renderListItem(item)}
          {...commontStorageItemsLayoutProps}
        />
      )
    } else {
      return (
        <StorageItemsLayout
          Skeleton={DocumentGridItemPlaceholder}
          renderItem={item => renderGridItem(item, isMobile)}
          {...commontStorageItemsLayoutProps}
        />
      )
    }
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>
      <Footer />
    </>
  )
}
